import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useUpdateEffect } from 'react-use';
import { Modal, Button, Typography, CheckBox, LazyLoadImage, Select } from 'ck-ui';
import { getImgixUrl } from 'ck-utils';
import { isEmailValid } from 'ck-utils/validate';
import layout from 'ck-utils/layout.module.scss';
import { TermsAndPrivacyLinks } from '../TermsAndPrivacyLinks';
import styles from './WishListPopup.module.scss';

const newList = { lname: 'My Favorites', products: [], new: true, checked: true };
const WishListPopup = ({ acceptsMarketing }) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSignedUp, setHasSignedUp] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [wishlists, setWishlists] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [selectedVariantIdError, setSelectedVariantIdError] = useState(null);

  const validateSelectedVariantId = () => {
    setSelectedVariantIdError(selectedVariantId || !show ? null : 'Please select a size.');
    return Boolean(selectedVariantId);
  };

  useEffect(() => {
    if (acceptsMarketing || localStorage.getItem('wishlistSignedUp') === 'true') {
      setHasSignedUp(true);
    }

    const handleShowWishListPopup = e => {
      const { product, wishListCollections, initialVariantId } = e.detail;
      const variantId = initialVariantId || wishListCollections.reduce(
        (result, list) => list.products.reduce(
          (acc, p) => (product.id === p.id ? p.variantId : acc),
          result
        ), '');
      setCurrentProduct(product);
      setSelectedVariantId(variantId);
      setWishlists(
        wishListCollections.length
          ? wishListCollections.map((list) => {
            const added = list.products.some((p) => p.variantId === variantId);
            return { ...list, added, checked: added, new: false };
          })
          : [newList]
      );
      setShow(true);
    };

    window.addEventListener('showWishlistPopup', handleShowWishListPopup,);
    return () => {
      window.removeEventListener('showWishlistPopup', handleShowWishListPopup);
    };
  }, [acceptsMarketing]);

  useUpdateEffect(() => {
    validateSelectedVariantId();
  }, [selectedVariantId]);

  if (!currentProduct) return null;

  const onClose = () => {
    setShow(false);
    setLoading(false);
    setSelectedVariantId('');
    setSelectedVariantIdError(null);
  };
  const validateEmail = () => {
    const isValid = isEmailValid(email);
    setEmailError(isValid ? null : 'Please enter a valid email address.');
    return isValid;
  };

  const { removingListIds, addingListIds, hasError, newListObj, hasExistingList } = wishlists.reduce((acc, item) => {
    // for removing a product from multiple lists
    if (!item.checked && item.added) acc.removingListIds.push(item.lid);
    // for adding a product to multiple lists
    if (item.checked && !item.added && item.lid) acc.addingListIds.push(item.lid);
    // for creating a new list
    if (item.new) acc.newListObj = item;
    // check if there are any errors
    if (item.error) acc.hasError = true;
    // check if there are existing list
    if (!item.new) acc.hasExistingList = true;
    return acc;
  }, { removingListIds: [], addingListIds: [], newListObj: {}, hasError: false, hasExistingList: false });

  const wishListProduct = {
    epi: selectedVariantId,
    empi: currentProduct.id,
    du: `https://${window.location.hostname}/products/${currentProduct.handle}`
  };

  // Get latest wishlists and close the modal
  const getLatestListAndCloseModal = () => {
    window.dispatchEvent(new CustomEvent('getLatestWishLists'));
    onClose();
  };

  const addProductToLists = (swat, listIds) => {
    if (listIds.length === 0) {
      getLatestListAndCloseModal();
    } else {
      swat.addProductToLists(wishListProduct, listIds, () => {
        getLatestListAndCloseModal();
        window.dataLayer.push({
          event: 'add_to_wishlist',
          ecommerce: {
            currencyCode: window.Shopify.currency,
            wishlist: {
              value: currentProduct.price,
              items: [
                {
                  item_id: currentProduct.id,
                  item_name: currentProduct.title,
                  price: currentProduct.price,
                  index: 0,
                  item_category: currentProduct.category,
                  item_brand: currentProduct.brand,
                  item_variant: selectedVariantId,
                  item_list_name: currentProduct.listName,
                  item_list_id: currentProduct.listId,
                  quantity: 1
                }
              ]
            }
          },
        });
      });
    }
  };

  const createNewList = () => {
    window.theme.useSwat((swat) => {
      if (newListObj?.new) {
        swat.createList({ lname: newListObj.lname }, (data) => {
          addProductToLists(swat, newListObj.checked ? [...addingListIds, data.lid] : addingListIds);
        });
      } else {
        addProductToLists(swat, addingListIds);
      }
    });
  };

  return (
    <Modal show={show} onClose={onClose} isTabletAboveFixedSize>
      <div className={styles.root}>
        <div className="is-flex">
          <LazyLoadImage
            src={`${getImgixUrl(currentProduct.image, [
              'fit=crop',
              'h=60',
              'w=60'
            ])}`}
            className={styles.circle}
            alt={currentProduct.title}
          />
          <Typography variant="heading7" className={styles.lh}>{currentProduct.title}</Typography>
        </div>
        <div className={styles.divider} />
        <Select
          size="medium"
          fullwidth
          className={classnames('modal-size-selector mt-4', { error: selectedVariantIdError })}
          value={selectedVariantId}
          onChange={(e) => {
            const { value } = e.target;
            setSelectedVariantId(value);
            setWishlists(wishlists.map((list) => {
              const added = list.products.some((product) => product.variantId === value);
              return ({ ...list, added, checked: added });
            }));
          }}
        >
          <option key="default" value="">Select size</option>
          {currentProduct.variants.map((variant) => (
            <option
              key={variant.id}
              value={`${variant.id}`.replace('gid://shopify/ProductVariant/', '')}
            >
              {variant.title}
            </option>
          ))}
        </Select>
        {selectedVariantIdError && (
          <Typography className="mt-1" variant="body-small" display="block" color="danger">{selectedVariantIdError}</Typography>
        )}
        {wishlists.map((item, index) => {
          const textBadge = (
            <Typography className={classnames(styles.circle, styles.bg, 'is-flex-centered')} uppercase variant="body-small" color="white">
              {item.lname?.split(' ').slice(0, 2).map(word => word[0]).join('')}
            </Typography>
          );

          const checkOnChange = () => setWishlists(wishlists.map((ele, i) => (i === index ? { ...ele, checked: !item.checked } : ele)));

          return (
            <div key={item.lid || index} className={styles.item}>
              <div className="is-flex">
                <div
                  role="button"
                  tabIndex="0"
                  onClick={() => {
                    if (item.new) return;
                    checkOnChange();
                  }}
                  className={classnames('is-flex', styles.left)}
                >
                  {item.products[0]?.image && !item.new ? (
                    <LazyLoadImage
                      src={`${getImgixUrl(item.products[0]?.image, [
                        'fit=crop',
                        'h=60',
                        'w=60'
                      ])}`}
                      className={styles.circle}
                      alt={currentProduct.title}
                    />
                  ) : textBadge}
                  {item.new ? (
                    <input
                      type="text"
                      /* eslint-disable-next-line jsx-a11y/no-autofocus */
                      autoFocus
                      value={item.lname}
                      className={classnames(styles.input, { [styles.error]: item.error })}
                      onChange={(e) => {
                        const lname = e.target.value;
                        let error = '';
                        if (lname.length < 3 || lname > 50) {
                          error = 'List name must have min length 3, max length 50';
                        }
                        setWishlists(wishlists.map(
                          (ele, i) => (i === index
                            ? { ...ele, lname, error: wishlists.find(l => l.lname === lname) ? 'List name already exists' : error }
                            : ele)));
                      }}
                    />
                  )
                    : <Typography className={styles.lh}> {item.lname} ({item.products.length})</Typography>}
                </div>
                <label>
                  <CheckBox
                    checked={item.checked}
                    onChange={checkOnChange}
                  />
                </label>
              </div>
              {item.error && (
                <Typography className={classnames('mt-1', styles.ml)} variant="body-small" display="block" color="danger">{item.error}</Typography>
              )}
            </div>
          );
        })}
        {!hasSignedUp && (
          <div className="mt-4">
            <Typography variant="heading8">Remind Me About My Wishlist</Typography>
            <label htmlFor="email"><Typography color="black-bis" weight={400}>Receive updates about your Wishlist contents via email</Typography></label>
            <input
              id="email"
              value={email}
              type="email"
              placeholder="Email Address"
              className={classnames(styles.input, 'is-inline-block', 'is-full-width', 'body-small', { [styles.error]: emailError })}
              onChange={e => setEmail(e.target.value)}
              onBlur={validateEmail}
            />
            {emailError && (
              <Typography className="mt-1" variant="body-small" display="block" color="danger">{emailError}</Typography>
            )}
            <TermsAndPrivacyLinks noSMS />
          </div>
        )}
        <div className={classnames('is-flex mt-3', hasExistingList ? layout.justifyBetween : layout.justifyCenter)}>
          {!hasExistingList ? <div /> : (
            <Button
              secondary
              disabled={hasError || wishlists.length === 10 || newListObj?.new}
              onClick={
                () => setWishlists(
                  [...wishlists, {
                    ...newList,
                    error: wishlists.find(item => item.lname === 'My Favorites') ? 'List name already exists' : '' }]
                )}
            >
              CREATE NEW LIST
            </Button>
          )}
          <Button
            loading={loading}
            disabled={
              hasError
                || (!removingListIds.length && !addingListIds.length && !newListObj?.new)
                || (!hasSignedUp && emailError) || selectedVariantIdError
            }
            onClick={() => {
              if (!validateSelectedVariantId(selectedVariantId)) return;
              setLoading(true);
              if (!hasSignedUp && validateEmail()) {
                window.theme.useSwat((swat) => {
                  swat.saveShopperContext(
                    email,
                    {
                      src: 'wlreminder',
                      app: 'Wishlist',
                      skipConnect: false,
                      acceptMarketing: true,
                    },
                    () => {
                      window._learnq = window._learnq || [];
                      window.klaviyo?.identify({
                        $email: email,
                      }, () => {
                        window._learnq.push(['track', 'Sign-up Form Submitted - Web', {
                          source: 'wishlist_popup'
                        }]);
                      });

                      localStorage.setItem('wishlistSignedUp', 'true');
                      setHasSignedUp(true);
                    }
                  );
                });
              }
              if (removingListIds.length) {
                window.theme.useSwat((swat) => {
                  // remove a product from multiple lists
                  swat.removeProductFromLists(wishListProduct, removingListIds, () => {
                    // after removing, dispatch create new list API
                    createNewList();
                  });
                });
              } else {
                createNewList();
              }
            }}
          >
            {removingListIds.length ? 'UPDATE LISTS' : 'ADD TO LIST'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

WishListPopup.propTypes = {
  acceptsMarketing: PropTypes.bool.isRequired
};

export { WishListPopup };
